import React, { useContext, useState, useEffect, Fragment } from "react";
import {
  AlertCircle,
  CheckCircle,
  Shield,
  Clock,
  Home,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import { AuthContext } from "./context/AuthContext";
import axios from "axios";
import config from "./config/environment";
import {
  handleApiError,
  handlePaddleError,
  logError,
  withErrorHandling,
} from "./utils/errorHandler";

// Create a global flag to prevent success redirects if there's a duplicate subscription
window.duplicateSubscriptionDetected = false;

// Helper Components
const Header = ({ firstName, handleLogout }) => (
  <nav className="bg-white border-b border-gray-200 px-4 py-2.5 shadow-sm relative z-50">
    <div className="flex justify-between items-center w-full">
      {/* Left side: Logo and Navigation */}
      <div className="flex items-center gap-2">
        <Link to="/" className="flex-shrink-0">
          <div className="flex items-center text-5xl font-extrabold">
            SQL
            <div className="relative bg-blue-100 text-blue-800 text-xl font-semibold me-2 px-2.5 py-0.5 rounded ms-2 flex items-center">
              Guroo
              {firstName && (
                <span className="ml-2 text-blue-500 font-normal">
                  | {firstName}
                </span>
              )}
              <div className="absolute -top-3 -right-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-8 h-8"
                  viewBox="0 0 24 24"
                  style={{
                    transform: "rotate(25deg)",
                    color: "#1a1a1a", // Darker color
                  }}
                >
                  {/* Main cap */}
                  <path
                    fill="currentColor"
                    d="M12 2L1 7l11 5 11-5-11-5zm0 6.5l-8-3.64V15l8 3.64 8-3.64V4.86l-8 3.64z"
                  />
                  {/* Tassel */}
                  <path
                    d="M21 7L21.5 8L27.5 24"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* Tassel knot */}
                  <circle cx="21" cy="7" r="0.5" fill="currentColor" />
                </svg>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Right side: User Menu */}
      <div className="flex items-center">
        <UserMenu handleLogout={handleLogout} />
      </div>
    </div>
  </nav>
);

const UserMenu = ({ handleLogout }) => (
  <Menu as="div" className="relative ml-3">
    <MenuButton className="flex items-center gap-2 p-2 rounded-full hover:bg-gray-100 transition-colors">
      {/* User Avatar */}
      <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
        <span className="text-white text-sm font-medium">
          {/* Get first letter of user's name */}
          {localStorage.getItem("firstName")?.charAt(0) || "U"}
        </span>
      </div>
      {/* Dropdown Arrow */}
      <svg
        className="w-4 h-4 text-gray-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </MenuButton>

    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <MenuItems className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        {/* User Info */}
        <div className="px-4 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">
            {localStorage.getItem("firstName") || "User"}
          </p>
          <p className="text-xs text-gray-500">
            {localStorage.getItem("username") || "user@example.com"}
          </p>
        </div>

        {/* Menu Items */}
        <MenuItem>
          {({ active }) => (
            <Link
              to="/profile"
              className={`${
                active ? "bg-gray-100" : ""
              } flex items-center px-4 py-2 text-sm text-gray-700`}
            >
              <svg
                className="mr-3 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Profile
            </Link>
          )}
        </MenuItem>

        <MenuItem>
          {({ active }) => (
            <Link
              to="/settings"
              className={`${
                active ? "bg-gray-100" : ""
              } flex items-center px-4 py-2 text-sm text-gray-700`}
            >
              <svg
                className="mr-3 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Settings
            </Link>
          )}
        </MenuItem>

        <div className="border-t border-gray-100">
          <MenuItem>
            {({ active }) => (
              <button
                onClick={handleLogout}
                className={`${
                  active ? "bg-gray-100" : ""
                } flex w-full items-center px-4 py-2 text-sm text-red-700`}
              >
                <svg
                  className="mr-3 h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                Sign out
              </button>
            )}
          </MenuItem>
        </div>
      </MenuItems>
    </Transition>
  </Menu>
);

const LoadingSpinner = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center flex-col py-8">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
    <p className="text-gray-600">{message}</p>
  </div>
);

const PlanCard = ({
  plan,
  isSelected,
  onSelect,
  details,
  price,
  regularPrice,
  savings,
  features,
  badge,
}) => (
  <div
    className={`relative p-4 rounded-lg cursor-pointer transition-all duration-300 border ${
      isSelected
        ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md transform scale-105"
        : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm"
    }`}
    onClick={onSelect}
  >
    {badge && (
      <div className="absolute -top-1 -right-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-lg">
        {badge}
      </div>
    )}

    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center space-x-2">
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
            isSelected ? "border-blue-500 bg-blue-500" : "border-gray-300"
          }`}
        >
          {isSelected && (
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
          )}
        </div>
        <h3 className="text-base font-semibold text-gray-900">{details}</h3>
      </div>
    </div>

    <div className="mb-3">
      <div className="flex items-baseline space-x-1">
        <span className="text-2xl font-bold text-gray-900">${price}</span>
        <span className="text-sm text-gray-500 font-medium">
          {plan === "yearly" ? "/year" : "/month"}
        </span>
      </div>

      {regularPrice && (
        <div className="mt-1 space-y-1">
          <p className="text-xs text-gray-400 line-through">
            ${regularPrice}/{plan === "yearly" ? "year" : "month"}
          </p>
          <div className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            Save ${savings}/year
          </div>
        </div>
      )}
    </div>

    <div className="space-y-2">
      {features.map((feature, index) => (
        <div
          key={index}
          className={`flex items-center text-xs ${
            feature.highlight ? "text-green-600 font-medium" : "text-gray-600"
          }`}
        >
          <CheckCircle
            className={`h-3 w-3 ${
              feature.highlight ? "text-green-500" : "text-blue-500"
            } mr-2 flex-shrink-0`}
          />
          <span>{feature.text}</span>
        </div>
      ))}
    </div>
  </div>
);

const ErrorDisplay = ({ type, message, onDashboardClick, onRetryClick }) => {
  let title = "Subscription Error";
  let description = message || "There was a problem with your subscription.";

  if (type === "duplicate") {
    title = "Duplicate Subscription Detected";
    description =
      message ||
      "Our system detected that you already have an active subscription.";
  } else if (type === "notfound") {
    title = "Subscription Not Found";
    description =
      message ||
      "Your subscription was not created. This could happen if your payment was declined.";
  } else if (type === "backend") {
    title = "Backend Processing Error";
    description =
      message ||
      "Your payment was processed, but we couldn't record your subscription in our system.";
  } else if (type === "verification") {
    title = "Verification Error";
    description =
      message ||
      "Your payment was processed, but we couldn't verify your subscription status.";
  }

  return (
    <div className="text-center space-y-3 my-4 bg-red-50 p-4 rounded-lg border border-red-100">
      <div className="flex justify-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
      </div>
      <h3 className="text-base font-semibold text-red-700">{title}</h3>
      <p className="text-sm text-gray-700">{description}</p>
      <p className="text-xs text-gray-600">
        If you believe this is an error, please contact our support team.
      </p>
      <div className="flex justify-center space-x-3 mt-3">
        <button
          onClick={onDashboardClick}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition shadow-sm text-sm"
        >
          Go to Dashboard
        </button>
        <button
          onClick={onRetryClick}
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition text-sm"
        >
          Try Again
        </button>
      </div>
    </div>
  );
};

const SuccessDisplay = ({ onDashboardClick }) => (
  <div className="text-center space-y-3 my-6">
    <div className="flex justify-center">
      <CheckCircle className="h-12 w-12 text-green-500" />
    </div>
    <h3 className="text-lg font-medium text-gray-900">Payment Successful!</h3>
    <p className="text-sm text-gray-600">
      Thank you for your purchase. You will receive a confirmation email
      shortly.
    </p>
    <button
      onClick={onDashboardClick}
      className="px-5 py-2 mt-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition shadow-sm"
    >
      Go to Dashboard
    </button>
  </div>
);

const ActiveSubscriptionDisplay = ({
  details,
  onDashboardClick,
  onRefreshClick,
}) => {
  // Helper functions for subscription display
  const getStatusMessage = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "trialing":
        return "Trial Period";
      case "past_due":
        return "Payment Past Due";
      case "pending":
        return "Processing Payment";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600";
      case "trialing":
        return "text-blue-600";
      case "past_due":
        return "text-orange-600";
      case "pending":
        return "text-yellow-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-4">
      <div className="flex items-start">
        <Shield className="h-6 w-6 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
        <div>
          <h3 className="font-medium text-blue-800 text-lg mb-2">
            You already have a subscription
          </h3>
          <div className="bg-white rounded-md p-4 border border-blue-100 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Plan:</span>
              <span className="font-medium text-gray-900 capitalize">
                {details?.plan_type || "Premium"}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Status:</span>
              <span
                className={`font-medium ${getStatusColor(
                  details?.status || "active"
                )}`}
              >
                {getStatusMessage(details?.status || "active")}
              </span>
            </div>
            {details?.ends_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Valid until:</span>
                <span className="font-medium text-gray-900">
                  {new Date(details.ends_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}
          </div>

          {details?.status === "pending" && (
            <div className="mt-3 p-4 bg-yellow-50 border border-yellow-100 rounded-md mb-4">
              <div className="flex items-start">
                <Clock className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-yellow-800 font-medium">
                    Your payment is being processed
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    This usually takes a few minutes. You'll receive access once
                    the payment is confirmed.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-3 mt-4">
            <button
              onClick={onDashboardClick}
              className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Home className="w-4 h-4" />
              Return to Dashboard
            </button>

            {details?.status === "pending" && (
              <button
                onClick={onRefreshClick}
                className="flex items-center justify-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Check Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component
const PaddlePaymentForm = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [paddleLoaded, setPaddleLoaded] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [activeSubscriptionDetails, setActiveSubscriptionDetails] =
    useState(null);
  const [duplicateError, setDuplicateError] = useState(false);
  const [errorType, setErrorType] = useState(null);

  const { logout, userId, firstName } = useContext(AuthContext);
  const navigate = useNavigate();

  // Paddle configuration from environment
  const PADDLE_CLIENT_TOKEN = config.paddle.clientToken;
  const PADDLE_ENVIRONMENT = config.paddle.environment;
  const PADDLE_SCRIPT_ID = "paddle-js";

  // Define subscription plans from environment
  const SUBSCRIPTION_PLANS = config.paddle.plans;

  // Function to manually check subscription status
  const checkExistingSubscription = async () => {
    if (!userId) return;

    setCheckingSubscription(true);
    try {
      const response = await axios.get(
        `${config.api.baseUrl}/api/subscriptions/check?userId=${userId}`,
        { timeout: config.api.timeout }
      );

      if (response.data.hasActiveSubscription) {
        setHasActiveSubscription(true);
        setActiveSubscriptionDetails(response.data.subscription);
      } else {
        setHasActiveSubscription(false);
        setActiveSubscriptionDetails(null);
      }
    } catch (err) {
      console.error("Error checking subscription status:", err);
      setHasActiveSubscription(false);
      setActiveSubscriptionDetails(null);
    } finally {
      setCheckingSubscription(false);
    }
  };

  // Check for existing subscriptions
  useEffect(() => {
    checkExistingSubscription();
  }, [userId]);

  const handleCheckoutSuccess = async (checkoutData) => {
    try {
      setLoading(true);
      setSuccess(true); // Show success state immediately
      console.log("Checkout success data:", checkoutData);
      const selectedPlanType =
        selectedPlan === "monthly" ? "monthly" : "yearly";

      // Calculate dates (these will be validated server-side)
      const startDate = new Date();
      const endDate =
        selectedPlan === "monthly"
          ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
          : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 365 days

      // With Paddle v2, initial checkout doesn't provide a subscription ID
      const subscriptionId = null; // This will be populated by webhook later

      // Extract email from checkout data or use stored username
      const email =
        checkoutData.data?.customer?.email || localStorage.getItem("username");

      localStorage.removeItem("checkout_error");

      try {
        // Format the request according to our updated backend
        console.log("Sending subscription creation request with data:", {
          userId,
          checkoutDataSummary: {
            transactionId:
              checkoutData.data?.transaction_id || checkoutData.data?.id,
            customerId: checkoutData.data?.customer?.id,
          },
          planType: selectedPlanType,
          email,
        });

        const response = await axios.post(
          `${config.api.baseUrl}/api/subscriptions/create`,
          {
            userId: userId,
            checkoutData: checkoutData.data,
            planType: selectedPlanType,
            email: email,
            subscriptionId: subscriptionId,
            startDate: startDate,
            endDate: endDate,
          }
        );

        console.log("Subscription record created:", response.data);

        // Don't try to verify - just redirect to success page
        console.log("Redirecting to payment success page...");

        // Try multiple redirect methods to ensure it works
        try {
          // Method 1: Direct window location change
          window.location.href = `${window.location.origin}/paymentsuccess`;
        } catch (e) {
          console.error("Direct redirect failed, trying navigate:", e);
          // Method 2: Use React Router navigate as fallback
          navigate("/paymentsuccess");
        }
        return;
      } catch (error) {
        console.error("Error creating subscription record:", error);

        // Check if this is a duplicate error
        if (
          error.response?.data?.message?.includes("already has a subscription")
        ) {
          console.log("Detected duplicate subscription error from server");
          window.duplicateSubscriptionDetected = true;
          localStorage.setItem("checkout_error", "duplicate");
          window.location.href = `${window.location.origin}/subscription?error=duplicate`;
          return;
        }

        // For other specific error messages
        if (error.response?.data?.message?.includes("database error")) {
          localStorage.setItem("checkout_error", "backend");
          window.location.href = `${window.location.origin}/subscription?error=backend`;
          return;
        }

        // For all other errors, go to success page anyway - the webhook will fix it
        console.log("Proceeding to success page despite server error");

        // Try multiple redirect methods to ensure it works
        try {
          window.location.href = `${window.location.origin}/paymentsuccess`;
        } catch (e) {
          console.error("Direct redirect failed, trying navigate:", e);
          navigate("/paymentsuccess");
        }
        return;
      }
    } catch (error) {
      console.error("Error in overall checkout success flow:", error);
      setLoading(false);
      setError(
        "An error occurred while processing your subscription. Please try again or contact support."
      );
    }
  };

  // Function to get Paddle customer ID for current user
  const getPaddleCustomerId = async () => {
    if (!userId) {
      // For users not logged in, return empty object
      return {};
    }

    try {
      // Check if user has a subscription with Paddle customer ID
      const response = await axios.get(
        `${config.api.baseUrl}/api/subscriptions/check?userId=${userId}`,
        { timeout: config.api.timeout }
      );

      if (response.data.hasActiveSubscription && response.data.subscription) {
        // Look for paddle_customer_id in subscription data
        const paddleCustomerId = response.data.subscription.paddle_customer_id;
        if (paddleCustomerId && paddleCustomerId.startsWith("ctm_")) {
          console.log("Found Paddle customer ID:", paddleCustomerId);
          return { id: paddleCustomerId };
        }
      }

      // If no Paddle customer ID found, return empty object (as per Paddle docs)
      console.log(
        "No Paddle customer ID found, using empty object for pwCustomer"
      );
      return {};
    } catch (error) {
      console.log("Could not fetch Paddle customer ID:", error);
      // Return empty object on error (safer than null)
      return {};
    }
  };

  // Load Paddle script
  useEffect(() => {
    const loadPaddle = async () => {
      try {
        const existingScript = document.getElementById(PADDLE_SCRIPT_ID);
        if (existingScript) return;

        const paddleScript = document.createElement("script");
        paddleScript.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
        paddleScript.id = PADDLE_SCRIPT_ID;
        paddleScript.async = true;

        const scriptLoaded = new Promise((resolve, reject) => {
          paddleScript.addEventListener("load", resolve);
          paddleScript.addEventListener("error", reject);
        });

        document.body.appendChild(paddleScript);
        await scriptLoaded;

        if (window.Paddle) {
          // Only set environment for sandbox, production uses default
          if (PADDLE_ENVIRONMENT === "sandbox") {
            window.Paddle.Environment.set("sandbox");
          }
          await window.Paddle.Setup({
            token: PADDLE_CLIENT_TOKEN,
            // Pass customer info for Retain integration (required for production)
            // pwCustomer requires Paddle customer ID (ctm_xxx), not username
            pwCustomer: await getPaddleCustomerId(),
            eventCallback: function (data) {
              console.log("Paddle Event:", data);
              if (data.name === "checkout.completed") {
                console.log("Checkout completed:", data);
                localStorage.setItem(
                  "paddleCheckoutData",
                  JSON.stringify(data)
                );
                localStorage.setItem("selectedPlan", selectedPlan);

                // Send the checkout details to backend
                handleCheckoutSuccess(data);

                // Only let Paddle redirect if we haven't detected a duplicate
                if (window.duplicateSubscriptionDetected) {
                  console.log(
                    "Blocking redirect due to duplicate subscription"
                  );
                  return false;
                }

                // Force redirect to success page
                console.log(
                  "Paddle checkout completed, forcing redirect to success page"
                );
                setTimeout(() => {
                  window.location.href = `${window.location.origin}/paymentsuccess`;
                }, 1000);
              }
            },
          });
          console.log("Paddle loaded and initialized");
          setPaddleLoaded(true);
        }
      } catch (err) {
        console.error("Error loading Paddle:", err);
        setError("Failed to load payment system");
      }
    };

    loadPaddle();

    return () => {
      const scriptElement = document.getElementById(PADDLE_SCRIPT_ID);
      if (scriptElement && scriptElement.parentNode) {
        scriptElement.parentNode.removeChild(scriptElement);
      }
    };
  }, [selectedPlan]);

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // First, check if the user can proceed with checkout
      console.log("Checking pre-checkout with userId:", userId);
      const preCheckUrl = `https://sqlguroo.com/api/subscriptions/pre-checkout?userId=${userId}`;
      console.log("Pre-checkout URL:", preCheckUrl);

      try {
        const preCheckResponse = await axios.get(preCheckUrl);
        console.log("Pre-checkout response:", preCheckResponse.data);

        if (!preCheckResponse.data.canProceed) {
          // User already has a subscription or there was an error
          setDuplicateError(true);

          if (preCheckResponse.data.status === "error") {
            // Handle specific error types
            if (preCheckResponse.data.errorCode === "DB_ERROR") {
              setErrorType("backend");
              setError(
                "Database error occurred. Please try again later or contact support."
              );
            } else {
              setErrorType("general");
              setError(
                preCheckResponse.data.message ||
                  "Error checking subscription status"
              );
            }
          } else {
            // This is likely a case where user already has subscription
            setErrorType("duplicate");
            setError(
              preCheckResponse.data.message ||
                "You already have an active subscription."
            );
          }

          // Get subscription details if available
          if (preCheckResponse.data.subscription) {
            setActiveSubscriptionDetails(preCheckResponse.data.subscription);
            setHasActiveSubscription(true);
          }

          // Store error in localStorage
          localStorage.setItem(
            "checkout_error",
            preCheckResponse.data.message ||
              "Error checking subscription status"
          );
          window.duplicateSubscriptionDetected = true;

          setLoading(false);
          return;
        }
      } catch (preCheckError) {
        // If the API check fails, assume no subscription exists and proceed
        console.log(
          "Pre-checkout check failed but proceeding anyway:",
          preCheckError
        );
      }

      if (!window.Paddle) {
        throw new Error("Paddle SDK not loaded");
      }

      const email = e.target.email.value;
      const plan = SUBSCRIPTION_PLANS[selectedPlan];

      const checkoutOptions = {
        items: [
          {
            priceId: plan.priceId,
            quantity: 1,
          },
        ],
        customer: {
          email: email,
        },
        settings: {
          displayMode: "overlay",
          theme: "light",
          locale: "en",
          successUrl: `${window.location.origin}/paymentsuccess`,
          allowLogout: false,
        },
      };

      console.log("Opening checkout with:", checkoutOptions);
      await window.Paddle.Checkout.open(checkoutOptions);
    } catch (err) {
      console.error("Checkout error:", err);
      setError(`Payment failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.clear();
    await logout();
    navigate("/");
  };

  // Handle URL error params
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const errorParam = queryParams.get("error");

    if (errorParam === "duplicate") {
      setErrorType("duplicate");
      setError(
        "You already have an active subscription. Please check your account details."
      );
      checkExistingSubscription();
    } else if (errorParam === "notfound") {
      setErrorType("notfound");
      setError(
        "Your subscription was not created. This could happen if your payment was declined or there was a system error."
      );
    } else if (errorParam === "backend") {
      setErrorType("backend");
      setError(
        "Your payment was processed, but we could not record your subscription. Please contact support with your transaction details."
      );
    } else if (errorParam === "verification") {
      setErrorType("verification");
      setError(
        "Your payment was processed, but we could not verify your subscription in our system. Please contact support for assistance."
      );
    } else if (errorParam === "general") {
      setErrorType("general");
      setError(
        "There was a problem with your subscription. Please contact support for assistance."
      );
    }

    if (errorParam) {
      setDuplicateError(true);
    }
  }, [navigate]);

  // Development mode API test function
  const testApiConnection = async () => {
    try {
      setLoading(true);
      setError("");

      const testResponse = await axios.get("https://sqlguroo.com/api/test");
      console.log("Test API response:", testResponse.data);

      const preCheckUrl = `https://sqlguroo.com/api/subscriptions/pre-checkout?userId=${userId}`;
      const preCheckResponse = await axios.get(preCheckUrl);
      console.log("Pre-checkout response:", preCheckResponse.data);

      alert("API connection successful! Check console for details.");
    } catch (error) {
      console.error("API test error:", error);
      setError(`API test failed: ${error.message}`);

      if (error.response) {
        console.log("Error response:", error.response.data);
        console.log("Error status:", error.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate monthly plan features
  const monthlyFeatures = [
    { text: "Full access to all features" },
    { text: "Cancel anytime" },
    { text: "Billed monthly" },
    { text: "Support via messages" },
  ];

  // Generate yearly plan features
  const yearlyFeatures = [
    { text: "Full access to all features" },
    { text: "Cancel anytime" },
    { text: "Billed annually" },
    { text: "Support via messages" },
  ];

  return (
    <div className="relative flex h-screen overflow-hidden bg-gray-50">
      {/* Main container */}
      <div
        className="relative flex flex-col 
                w-full 
                max-w-[98%]            
                2xl:max-w-[1820px]     
                mx-auto 
                h-screen 
                overflow-hidden"
      >
        <Header firstName={firstName} handleLogout={handleLogout} />

        <main className="mx-auto w-full h-full overflow-auto py-2">
          <div className="h-full rounded-xl border border-gray-200 bg-white shadow overflow-hidden">
            <div className="h-full flex flex-col p-4">
              {/* Header/Intro section */}
              <div className="text-center mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-1">
                  Choose Your Subscription Plan
                </h1>
                <p className="text-sm text-gray-600">
                  Unlock the full potential of SQL Guroo with unlimited access
                  to all features and premium content.
                </p>
              </div>

              {/* Test button in development mode */}
              {process.env.NODE_ENV !== "production" && (
                <div className="mb-3 p-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <button
                    type="button"
                    onClick={testApiConnection}
                    className="w-full px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Test API Connection
                  </button>
                </div>
              )}

              {checkingSubscription ? (
                <div className="flex justify-center items-center h-full">
                  <LoadingSpinner message="Checking subscription status..." />
                </div>
              ) : hasActiveSubscription ? (
                <div className="max-w-2xl mx-auto">
                  <ActiveSubscriptionDisplay
                    details={activeSubscriptionDetails}
                    onDashboardClick={() => navigate("/")}
                    onRefreshClick={() => window.location.reload()}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 h-full">
                  {/* Left side - Plan Selection */}
                  <div className="xl:col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 p-4 overflow-y-auto">
                    <h2 className="text-base font-semibold text-gray-900 mb-3">
                      Select Your Plan
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <PlanCard
                        plan="monthly"
                        isSelected={selectedPlan === "monthly"}
                        onSelect={() => setSelectedPlan("monthly")}
                        details="Monthly Plan"
                        price={SUBSCRIPTION_PLANS.monthly.amount}
                        features={monthlyFeatures}
                      />

                      <PlanCard
                        plan="yearly"
                        isSelected={selectedPlan === "yearly"}
                        onSelect={() => setSelectedPlan("yearly")}
                        details="Yearly Plan"
                        price={SUBSCRIPTION_PLANS.yearly.amount}
                        regularPrice={228}
                        savings={
                          SUBSCRIPTION_PLANS.monthly.amount * 12 -
                          SUBSCRIPTION_PLANS.yearly.amount
                        }
                        features={yearlyFeatures}
                        badge="Best Value"
                      />
                    </div>
                  </div>

                  {/* Right side - Payment Form */}
                  <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4 overflow-y-auto">
                    {!success && duplicateError ? (
                      <ErrorDisplay
                        type={errorType}
                        message={error}
                        onDashboardClick={() => navigate("/")}
                        onRetryClick={() => window.location.reload()}
                      />
                    ) : !success ? (
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 mb-3">
                          Complete Purchase
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="space-y-1">
                            <label
                              htmlFor="email"
                              className="block text-sm font-semibold text-gray-700"
                            >
                              Email Address
                            </label>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              defaultValue={
                                localStorage.getItem("username") || ""
                              }
                              placeholder="Enter your email address"
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white shadow-sm text-sm"
                            />
                            <p className="text-xs text-gray-500">
                              Your receipt and subscription details will be sent
                              here
                            </p>
                          </div>

                          {error && (
                            <div className="flex items-center p-3 bg-red-50 rounded-md border border-red-100">
                              <AlertCircle
                                size={14}
                                className="text-red-500 mr-2 flex-shrink-0"
                              />
                              <span className="text-xs text-red-800">
                                {error}
                              </span>
                            </div>
                          )}

                          <div className="pt-2">
                            <button
                              type="submit"
                              disabled={loading || !paddleLoaded}
                              className={`w-full py-3 px-4 rounded-lg text-white font-semibold text-sm shadow-lg transition-all duration-200 transform ${
                                loading || !paddleLoaded
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 hover:scale-105 hover:shadow-xl"
                              }`}
                            >
                              {loading ? (
                                <div className="flex items-center justify-center">
                                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                  Processing...
                                </div>
                              ) : (
                                `Pay $${
                                  SUBSCRIPTION_PLANS[selectedPlan].amount
                                } ${
                                  selectedPlan === "monthly"
                                    ? "/month"
                                    : "/year"
                                }`
                              )}
                            </button>
                          </div>

                          {!paddleLoaded && (
                            <div className="flex items-center justify-center text-xs text-gray-500 pt-1">
                              <div className="animate-spin h-3 w-3 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                              Loading payment system...
                            </div>
                          )}

                          <div className="pt-3 border-t border-gray-200">
                            <div className="flex items-center justify-center space-x-2 bg-green-50 p-2 rounded-lg border border-green-100">
                              <Shield className="h-4 w-4 text-green-600" />
                              <p className="text-xs text-green-700 font-medium">
                                🔒 Secure payment powered by Paddle
                              </p>
                            </div>
                          </div>
                        </form>
                      </div>
                    ) : (
                      <SuccessDisplay onDashboardClick={() => navigate("/")} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaddlePaymentForm;
